import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { listProcurementOrders } from "../services/procurementService";
import {
  createSupplierInvoice,
  listSupplierInvoices,
} from "../services/supplierInvoiceService";
import SupplierInvoiceControlStatusBadge from "../components/supplier-invoices/SupplierInvoiceControlStatusBadge";
import SupplierInvoiceSeverityBadge from "../components/supplier-invoices/SupplierInvoiceSeverityBadge";

const INITIAL_FILTERS = {
  search: "",
  supplier: "",
  controlStatus: "",
  severity: "",
  onlyDifferences: "",
  onlyUnlinked: "",
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function shortId(value) {
  const text = String(value || "");
  if (!text) return "-";
  return text.slice(-8);
}

function normalizeSearch(value) {
  return String(value || "").trim().toLowerCase();
}

function buildEmptyLine() {
  return {
    description: "",
    quantity: "1",
    unit: "st",
    unitPrice: "",
    lineTotal: "",
  };
}

export default function SupplierInvoicesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [invoices, setInvoices] = useState([]);
  const [supplierMap, setSupplierMap] = useState({});
  const [procurementMap, setProcurementMap] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [procurementOrders, setProcurementOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({
    supplierOrganizationId: "",
    procurementOrderId: "",
    invoiceNumber: "",
    invoiceDate: "",
    totalAmount: "",
    currency: "EUR",
    lines: [buildEmptyLine()],
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [invoiceRes, organizationRes, procurementRes] = await Promise.all([
        listSupplierInvoices(),
        api.get("/organizations"),
        listProcurementOrders(),
      ]);
      const organizationItems = Array.isArray(organizationRes?.data?.items)
        ? organizationRes.data.items
        : [];
      const procurementItems = Array.isArray(procurementRes?.items) ? procurementRes.items : [];
      setInvoices(Array.isArray(invoiceRes?.items) ? invoiceRes.items : []);
      setOrganizations(organizationItems);
      setProcurementOrders(procurementItems);
      setSupplierMap(
        organizationItems.reduce((acc, item) => {
          acc[String(item._id)] = item.displayName || item.name || item.email || item._id;
          return acc;
        }, {})
      );
      setProcurementMap(
        procurementItems.reduce((acc, item) => {
          acc[String(item._id)] = item.orderNumber || item.title || item._id;
          return acc;
        }, {})
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Leveranciersfacturen laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleInvoices = useMemo(() => {
    const search = normalizeSearch(filters.search);
    const supplierSearch = normalizeSearch(filters.supplier);
    return invoices.filter((item) => {
      const supplierName =
        supplierMap[String(item.supplierOrganizationId)] ||
        String(item.supplierOrganizationId || "");
      const procurementLabel =
        procurementMap[String(item.procurementOrderId)] ||
        String(item.procurementOrderId || "");
      const haystack = [
        item.invoiceNumber,
        supplierName,
        procurementLabel,
        item.projectId,
        item.workorderId,
        item.blockedReasonMessage,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (search && !haystack.includes(search)) return false;
      if (supplierSearch && !supplierName.toLowerCase().includes(supplierSearch)) return false;
      if (filters.controlStatus && String(item.controlStatus || "") !== filters.controlStatus) return false;
      if (filters.severity && String(item?.differenceSummary?.severity || "") !== filters.severity) return false;
      if (filters.onlyDifferences === "true" && Number(item?.differenceSummary?.lineDifferenceTotal || 0) <= 0) return false;
      if (filters.onlyUnlinked === "true" && item.procurementOrderId) return false;
      return true;
    });
  }, [filters, invoices, procurementMap, supplierMap]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const supplierOptions = useMemo(
    () =>
      organizations.filter(
        (item) =>
          String(item.status || "active") === "active" &&
          (!Array.isArray(item.roles) || item.roles.includes("supplier"))
      ),
    [organizations]
  );

  const updateFormField = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const updateLineField = (index, key, value) => {
    setFormState((prev) => {
      const lines = Array.isArray(prev.lines) ? [...prev.lines] : [];
      const nextLine = { ...(lines[index] || buildEmptyLine()), [key]: value };
      if (key === "quantity" || key === "unitPrice") {
        const quantity = Number(key === "quantity" ? value : nextLine.quantity || 0);
        const unitPrice = Number(key === "unitPrice" ? value : nextLine.unitPrice || 0);
        nextLine.lineTotal = quantity > 0 && unitPrice >= 0 ? String(quantity * unitPrice) : "";
      }
      lines[index] = nextLine;
      return { ...prev, lines };
    });
  };

  const addLine = () => {
    setFormState((prev) => ({
      ...prev,
      lines: [...(Array.isArray(prev.lines) ? prev.lines : []), buildEmptyLine()],
    }));
  };

  const removeLine = (index) => {
    setFormState((prev) => {
      const nextLines = (Array.isArray(prev.lines) ? prev.lines : []).filter((_, i) => i !== index);
      return { ...prev, lines: nextLines.length ? nextLines : [buildEmptyLine()] };
    });
  };

  const resetForm = () => {
    setFormState({
      supplierOrganizationId: "",
      procurementOrderId: "",
      invoiceNumber: "",
      invoiceDate: "",
      totalAmount: "",
      currency: "EUR",
      lines: [buildEmptyLine()],
    });
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    const lines = (Array.isArray(formState.lines) ? formState.lines : [])
      .map((line, index) => ({
        lineNumber: index + 1,
        description: String(line.description || "").trim(),
        quantity: Number(line.quantity || 0),
        unit: String(line.unit || "").trim(),
        unitPrice: Number(line.unitPrice || 0),
        lineTotal: Number(line.lineTotal || 0),
      }))
      .filter((line) => line.description && line.quantity >= 0 && line.unitPrice >= 0);

    if (!formState.supplierOrganizationId || !formState.procurementOrderId) {
      setError("Leverancier en inkooporder zijn verplicht.");
      return;
    }
    if (!String(formState.invoiceNumber || "").trim() || !String(formState.invoiceDate || "").trim()) {
      setError("Factuurnummer en factuurdatum zijn verplicht.");
      return;
    }
    if (!lines.length) {
      setError("Voeg minimaal 1 factuurregel toe.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const created = await createSupplierInvoice({
        supplierOrganizationId: formState.supplierOrganizationId,
        procurementOrderId: formState.procurementOrderId,
        invoiceNumber: formState.invoiceNumber,
        invoiceDate: formState.invoiceDate,
        totalAmount: Number(formState.totalAmount || 0),
        currency: formState.currency || "EUR",
        lines,
      });
      setShowForm(false);
      resetForm();
      await load();
      if (created?._id) {
        navigate(`/pl/inkoop/facturen/${created._id}`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Leveranciersfactuur aanmaken mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Controller</p>
            <h1 className="text-2xl font-semibold text-slate-900">Factuurcontrole</h1>
            <p className="text-sm text-slate-600">
              Handmatig overzicht van leveranciersfacturen, koppelingen en prijsverschillen.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowForm((prev) => !prev)}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              {showForm ? "Formulier sluiten" : "Nieuwe leveranciersfactuur"}
            </button>
            <button
              type="button"
              onClick={load}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700"
            >
              Vernieuwen
            </button>
          </div>
        </div>
      </section>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

      {showForm && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Nieuwe leveranciersfactuur</h2>
            <p className="text-sm text-slate-600">
              MVP: handmatig invoeren en direct koppelen aan een bestaande inkooporder.
            </p>
          </div>

          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <select
                value={formState.supplierOrganizationId}
                onChange={(e) => updateFormField("supplierOrganizationId", e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">Kies leverancier</option>
                {supplierOptions.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.displayName || item.name || item.email || item._id}
                  </option>
                ))}
              </select>

              <select
                value={formState.procurementOrderId}
                onChange={(e) => updateFormField("procurementOrderId", e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">Kies inkooporder</option>
                {procurementOrders.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.orderNumber || item._id} · {item.title || item.supplierName || "-"}
                  </option>
                ))}
              </select>

              <input
                value={formState.invoiceNumber}
                onChange={(e) => updateFormField("invoiceNumber", e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Factuurnummer"
              />

              <input
                type="date"
                value={formState.invoiceDate}
                onChange={(e) => updateFormField("invoiceDate", e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />

              <input
                type="number"
                min="0"
                step="0.01"
                value={formState.totalAmount}
                onChange={(e) => updateFormField("totalAmount", e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Totaalbedrag"
              />

              <input
                value={formState.currency}
                onChange={(e) => updateFormField("currency", e.target.value.toUpperCase())}
                maxLength={3}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="EUR"
              />
            </div>

            <div className="rounded-xl border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Factuurregels</h3>
                <button
                  type="button"
                  onClick={addLine}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700"
                >
                  Regel toevoegen
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Omschrijving</th>
                      <th className="px-4 py-3 font-medium">Aantal</th>
                      <th className="px-4 py-3 font-medium">Eenheid</th>
                      <th className="px-4 py-3 font-medium">Prijs</th>
                      <th className="px-4 py-3 font-medium">Regeltotaal</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(Array.isArray(formState.lines) ? formState.lines : []).map((line, index) => (
                      <tr key={`line-${index}`}>
                        <td className="px-4 py-3">
                          <input
                            value={line.description}
                            onChange={(e) => updateLineField(index, "description", e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Omschrijving"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.quantity}
                            onChange={(e) => updateLineField(index, "quantity", e.target.value)}
                            className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={line.unit}
                            onChange={(e) => updateLineField(index, "unit", e.target.value)}
                            className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.unitPrice}
                            onChange={(e) => updateLineField(index, "unitPrice", e.target.value)}
                            className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.lineTotal}
                            onChange={(e) => updateLineField(index, "lineTotal", e.target.value)}
                            className="w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
                          >
                            Verwijderen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {submitting ? "Opslaan..." : "Factuur opslaan"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <input
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Zoek factuurnr, order, project..."
          />
          <input
            value={filters.supplier}
            onChange={(e) => updateFilter("supplier", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Leverancier"
          />
          <select
            value={filters.controlStatus}
            onChange={(e) => updateFilter("controlStatus", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Alle controlestatussen</option>
            <option value="received">Ontvangen</option>
            <option value="matched">Gekoppeld</option>
            <option value="within_tolerance">Binnen tolerantie</option>
            <option value="needs_review">Controleren</option>
            <option value="approved">Goedgekeurd</option>
            <option value="rejected">Afgewezen</option>
          </select>
          <select
            value={filters.severity}
            onChange={(e) => updateFilter("severity", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Alle afwijkingen</option>
            <option value="green">Groen</option>
            <option value="yellow">Geel</option>
            <option value="red">Rood</option>
          </select>
          <select
            value={filters.onlyDifferences}
            onChange={(e) => updateFilter("onlyDifferences", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Verschillen: alle</option>
            <option value="true">Alleen met verschil</option>
          </select>
          <select
            value={filters.onlyUnlinked}
            onChange={(e) => updateFilter("onlyUnlinked", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Koppeling: alle</option>
            <option value="true">Alleen niet gekoppeld</option>
          </select>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Factuur</th>
                <th className="px-4 py-3 font-medium">Leverancier</th>
                <th className="px-4 py-3 font-medium">Factuurdatum</th>
                <th className="px-4 py-3 font-medium">Inkooporder</th>
                <th className="px-4 py-3 font-medium">Bedrag</th>
                <th className="px-4 py-3 font-medium">Controlestatus</th>
                <th className="px-4 py-3 font-medium">Afwijking</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={7}>
                    Facturen laden...
                  </td>
                </tr>
              ) : visibleInvoices.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={7}>
                    Nog geen leveranciersfacturen gevonden.
                  </td>
                </tr>
              ) : (
                visibleInvoices.map((item) => {
                  const supplierName =
                    supplierMap[String(item.supplierOrganizationId)] ||
                    shortId(item.supplierOrganizationId);
                  const procurementLabel =
                    procurementMap[String(item.procurementOrderId)] ||
                    (item.procurementOrderId ? shortId(item.procurementOrderId) : "-");
                  return (
                    <tr key={item._id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link to={`/pl/inkoop/facturen/${item._id}`} className="font-medium text-slate-900 hover:underline">
                          {item.invoiceNumber || shortId(item._id)}
                        </Link>
                        <p className="mt-1 text-xs text-slate-500">
                          Status: {item.status || "-"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{supplierName}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDate(item.invoiceDate)}</td>
                      <td className="px-4 py-3 text-slate-700">{procurementLabel}</td>
                      <td className="px-4 py-3 text-slate-700">{formatMoney(item.totalAmount)}</td>
                      <td className="px-4 py-3">
                        <SupplierInvoiceControlStatusBadge status={item.controlStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <SupplierInvoiceSeverityBadge severity={item?.differenceSummary?.severity} />
                          <span className="text-xs text-slate-500">
                            {formatMoney(item?.differenceSummary?.lineDifferenceTotal || 0)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
